import cn from 'clsx';
import { DialogHTMLAttributes, FC, useCallback, useEffect, useMemo, useState } from 'react';

import CSSTransition from './CSSTransition';
import s from './Modal.module.scss';
import ModalCloseButton from './ModalCloseButton';

interface Props {
  visible?: boolean;
  scroll?: boolean;
  rebound?: boolean;
  animated?: boolean;
  fullScreen?: boolean;
  closeButton?: boolean;
  onCloseButtonClick?: () => void;
}

type NativeAttrs = Omit<DialogHTMLAttributes<HTMLElement>, keyof Props>;

export type ModalWrapperProps = Props & NativeAttrs;

const ModalWrapper: FC<React.PropsWithChildren<React.PropsWithChildren<ModalWrapperProps>>> = ({
  children,
  visible = false,
  fullScreen,
  closeButton,
  rebound = false,
  animated,
  onCloseButtonClick,
  scroll,
  ...props
}) => {
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setRendered(true);
      clearTimeout(timer);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    if (onCloseButtonClick) {
      onCloseButtonClick();
    }
  }, [onCloseButtonClick]);

  const getState = useMemo(() => {
    return visible ? 'open' : 'closed';
  }, [visible]);

  const renderChildren = useMemo(() => {
    return (
      <section
        tabIndex={-1}
        data-state={getState}
        role="dialog"
        aria-modal={visible}
        className={cn(s.dialog, {
          [s['dialog-rendered'] as string]: rendered,
          [s['dialog-rebound'] as string]: rebound
        })}
        {...props}
      >
        {closeButton && <ModalCloseButton onClick={handleClose} />} {children}
      </section>
    );
  }, [closeButton, handleClose, children, getState, rendered, rebound, visible, props]);

  const conditionalRender = useMemo(() => {
    if (animated) {
      return (
        <CSSTransition name="dialog-css" visible={visible} enterTime={20} leaveTime={20} clearTime={300} scss={s} className="">
          {renderChildren}
        </CSSTransition>
      );
    }
    return visible ? renderChildren : null;
  }, [animated, visible, renderChildren]);

  return conditionalRender;
};

export default ModalWrapper;
